import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CheckCircle, Sync } from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/ar';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/pt-br';
import 'moment/locale/ru';
import 'moment/locale/zh-cn';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from 'store';
import { selectCall, updateCallHistory } from 'store/call';

import styles from './NotesForm.module.scss';

export function NotesForm(): JSX.Element {
  const dispatch = useAppDispatch();
  const call = useAppSelector(selectCall);
  const { i18n } = useTranslation();

  const [notes, setNotes] = useState(call?.meetingNotes || '');
  const [startTyping, setStartTyping] = useState(false);
  const anchorRef = useRef<HTMLTextAreaElement>(null);

  const getMomentLocale = useCallback((): string => {
    const rawLang = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase();
    const baseLang = rawLang.split('-')[0];

    if (baseLang === 'br' || rawLang === 'pt-br' || baseLang === 'pt') {
      return 'pt-br';
    }

    if (baseLang === 'zh') {
      return 'zh-cn';
    }

    if (baseLang === 'ar' || baseLang === 'es' || baseLang === 'fr' || baseLang === 'ru') {
      return baseLang;
    }

    return 'en';
  }, [i18n.language, i18n.resolvedLanguage]);

  const callDate = useMemo(() => {
    const locale = getMomentLocale();
    let date = moment();
    if (call?.startDate) {
      date = moment(call?.startDate || moment(), 'YYYY-MM-DDTHH:mm:ss.SSSSZ');
    }
    return date.locale(locale).format('MMMM DD, YYYY [at] hh:mm A');
  }, [call?.startDate, getMomentLocale]);

  const updateNotes = useCallback(
    _.debounce((notes: string) => {
      setNotes(notes);

      dispatch(
        updateCallHistory({
          meetingNotes: notes,
        }),
      )
        .unwrap()
        .then(() => {
          setStartTyping(false);
        });
    }, 500),
    [],
  );

  useEffect(() => {
    if (anchorRef.current) {
      anchorRef.current && anchorRef.current.focus();
    }
  }, []);

  const handleNotesChange = (event: ChangeEvent<HTMLTextAreaElement>): any => {
    setStartTyping(true);
    updateNotes(event.target.value);
  };

  return (
    <Box className={styles.container}>
      <div className={styles.header}>
        <Typography component='p' variant='body3'>
          {callDate}
        </Typography>

        {startTyping ? <Sync className={styles.syncIcon} /> : <CheckCircle />}
      </div>

      <Box className={styles.form}>
        <Box className={styles.innerForm}>
          <textarea
            ref={anchorRef}
            placeholder={''}
            defaultValue={notes}
            onChange={handleNotesChange}
            className={styles.textField}
          />
        </Box>
      </Box>
    </Box>
  );
}
